import { computed } from 'vue';
import { usePermissionsStore as usePermissionsStoreState } from '@/stores/permissionsStore';
import { isGranularPermissionsEnabled } from '@/utils/granularPermissionsFlag';

export type PermissionsVersion = 'v2' | 'v3';

/**
 * Public gating surface for the current user's permissions.
 *
 * Resolves legacy (v2) vs v3 grants from the pinned granular-permissions flag.
 * An optional `version` override is a **downgrade only**: when the flag is off,
 * `'v2'` is always served. When the flag is on, omit `version` to get v3, or pass
 * `'v2'` so a call site whose backing endpoint is still on v2 can keep v2 semantics.
 *
 * Components import this composable — not the Pinia store — so the temporary
 * version argument can be removed after migration without renaming call sites.
 */
export function usePermissionsStore(version?: PermissionsVersion) {
  const store = usePermissionsStoreState();
  const flagOn = isGranularPermissionsEnabled();

  if (import.meta.env.DEV && version === 'v3' && !flagOn) {
    console.warn(
      '[usePermissionsStore] version "v3" requested while granular-permissions flag is off; serving v2',
    );
  }

  const resolved: PermissionsVersion = flagOn ? (version ?? 'v3') : 'v2';

  const permissions = computed(() => (
    resolved === 'v3' ? store.v3Grants : store.legacyGrants
  ));

  return {
    permissions,
    /**
     * Temporary (GM-3801). The dev mock drawer writes a draft the store prefers
     * over the `/me` mapper. Remove it in the permissions cleanup milestone.
     */
    setV3GrantsOverride: store.setV3GrantsOverride,
  };
}
